import { cva, type VariantProps } from 'class-variance-authority';
import { Sparkles } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/core/lib/utils';

const aiBadgeVariants = cva(
  // The gradient is a theme token, not a literal: it has to take a step down in dark, where
  // every other filled element sits lower than it does in light.
  'bg-[image:var(--ai-badge)] font-medium text-[color:var(--ai-badge-foreground)]',
  {
    variants: {
      size: {
        /** Inline next to a field label. */
        sm: 'gap-1 px-1.5 py-0 text-[10px] [&>svg]:size-2.5',
        /** Standalone, above a dialog title. */
        md: 'gap-1.5 px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

interface AiBadgeProps extends React.ComponentProps<'span'>, VariantProps<typeof aiBadgeVariants> {
  /** Already-translated caption — e.g. "Generado por IA". */
  label: string;
}

/**
 * Marks content an AI produced or suggested.
 *
 * The one gradient in the app, and the only place the AI accent is a colour of its own rather
 * than the brand accent. Both stops live in `index.css` so the dark theme can take them down a
 * step, the way it does for every other filled surface.
 */
export function AiBadge({ label, size, className, ...props }: AiBadgeProps) {
  return (
    // `ghost` rather than `outline`: the design draws no border, and outline's own border and
    // text colours would only be there to be overridden.
    <Badge variant="ghost" className={cn(aiBadgeVariants({ size }), className)} {...props}>
      {/* The colour spelled out on the icon itself, not inherited: dropdown items, selects and
          command lists all grey any descendant svg that carries no `text-` class, so the badge
          turned up with a grey sparkle wherever it sat inside one. */}
      <Sparkles className="text-[color:var(--ai-badge-foreground)]" aria-hidden />
      {label}
    </Badge>
  );
}
